import { FC, memo } from 'react';

import { Editor } from 'primereact/editor';

import { OButton, OInput, OResponsiveModal, OStepperInput } from '@src/shared';
import { SetupSection, TimelineSection } from './components';

import { useCreateCta } from './useCreateCta';

import styles from './createCtaModal.module.scss';

interface IProps {}

const CreateCtaModal: FC<IProps> = () => {
  const { formState, actions, isValid, isModalOpen } = useCreateCta();

  return (
    <OResponsiveModal
      isOpen={isModalOpen}
      onClose={actions.closeModal}
      title={formState.isEditMode ? 'Edit CTA' : 'Add CTA'}
      hasBackdrop
      drawerActions={{
        applyLabel: formState.isEditMode ? 'Save Changes' : 'Create CTA',
        cancelLabel: 'Cancel',
        onApply: actions.submitForm,
        onCancel: actions.closeModal,
      }}
      oModalProps={{
        classes: {
          body: styles.modalBody,
          modal: styles?.modalRoot,
          footer: styles.modalFooter,
          container: styles.modalContainer,
        },
        footer: (
          <>
            <OButton text="Cancel" onClick={actions.closeModal} variant="primary" outlined size="medium" />
            <OButton
              text={formState.isEditMode ? 'Save Changes' : 'Create CTA'}
              onClick={actions.submitForm}
              isDisabled={!isValid}
              variant="alternative"
              size="medium"
            />
          </>
        ),
      }}
    >
      <div className={styles.formContainer}>
        {/* Title */}
        <div className={styles.fieldGroup}>
          <span className={styles.label}>CTA Title</span>
          <OInput
            placeholder="Enter CTA name"
            value={formState.title}
            onChange={(e) => actions.setTitle(e.target.value)}
          />
        </div>

        {/* Duration */}
        <div className={styles.fieldGroup}>
          <span className={styles.label}>CTA Duration</span>
          <div className={styles.stepperRow}>
            <OStepperInput
              value={formState.duration.minutes}
              placeholder="Minutes"
              onIncrement={actions.incDurationMin}
              onDecrement={actions.decDurationMin}
              onFocus={actions.handleDurationFocus}
            />
            <OStepperInput
              value={formState.duration.seconds}
              placeholder="Seconds"
              onIncrement={actions.incDurationSec}
              onDecrement={actions.decDurationSec}
              onFocus={actions.handleDurationFocus}
            />
          </div>
          {formState.warning && <span className={styles.warning}>{formState.warning}</span>}
        </div>

        {/* Description */}
        <div className={styles.fieldGroup}>
          <span className={styles.label}>
            Description <span className={styles.labelTag}>(Optional)</span>
          </span>
          <div className={styles.editorContainer}>
            <Editor
              value={formState.description}
              onTextChange={(e) => actions.setDescription(e.htmlValue || '')}
              headerTemplate={
                <span className="ql-formats">
                  <button className="ql-bold" aria-label="Bold" />
                  <button className="ql-italic" aria-label="Italic" />
                  <button className="ql-underline" aria-label="Underline" />
                  <span className={styles.toolbarSeparator} />
                  <button className="ql-list" aria-label="List bullet" value="bullet" />
                  <button className="ql-list" aria-label="List ordered" value="ordered" />
                  <span className={styles.toolbarSeparator} />
                  <button className="ql-align" aria-label="Text Alignment left" />
                  <button className="ql-align" aria-label="Text Alignment center" value="center" />
                  <button className="ql-align" aria-label="Text Alignment right" value="right" />
                  <button className="ql-align" aria-label="Text Alignment justify" value="justify" />
                  <span className={styles.toolbarSeparator} />
                  <button className="ql-direction" aria-label="Right to Left" value="rtl" />
                  <button className="ql-direction" aria-label="Left to Right" value="ltr" />
                  <span className={styles.toolbarSeparator} />
                  <button className="ql-indent" aria-label="Indent left" value="+1" />
                  <button className="ql-indent" aria-label="Indent right" value="-1" />
                </span>
              }
            />
          </div>
        </div>

        {/* Starting In */}
        <div className={styles.fieldGroup}>
          <span className={styles.label}>
            Starting In <span className={styles.labelTag}>(After the meeting starts)</span>
          </span>
          <div className={styles.row}>
            <div className={styles.stepperRow}>
              <OStepperInput
                value={formState.startingIn.minutes}
                placeholder="Minutes"
                onIncrement={actions.incStartingMin}
                onDecrement={actions.decStartingMin}
                readOnly={formState.isLiveNow}
                disabled={formState.isLiveNow}
                onFocus={(e) => e.currentTarget.blur()}
              />
              <OStepperInput
                value={formState.startingIn.seconds}
                placeholder="Seconds"
                onIncrement={actions.incStartingSec}
                onDecrement={actions.decStartingSec}
                readOnly={formState.isLiveNow}
                disabled={formState.isLiveNow}
                onFocus={(e) => e.currentTarget.blur()}
              />
            </div>
            {/* - Only show if user is host and joined room */}
            {formState.shouldShowLiveNow && (
              <label className={styles.liveNowToggle}>
                <input type="checkbox" checked={formState.isLiveNow} onChange={actions.toggleLiveNow} />
                Live Now
              </label>
            )}
          </div>
        </div>

        {/* Timeline Placeholder */}
        <TimelineSection />

        {/* Setup Section */}
        <SetupSection
          buttonText={formState.buttonText}
          buttonUrl={formState.buttonUrl}
          onButtonTextChange={actions.setButtonText}
          onButtonUrlChange={actions.setButtonUrl}
          uploadedFiles={formState.uploadedFiles}
          onFilesChange={actions.setUploadedFiles}
          ctaDurationInSeconds={
            (Number(formState.duration.minutes) || 0) * 60 + (Number(formState.duration.seconds) || 0)
          }
          urlError={formState.urlError}
          onUrlBlur={actions.handleUrlBlur}
        />
      </div>
    </OResponsiveModal>
  );
};

export default memo(CreateCtaModal);
